import { CheckCircleIcon, CircleIcon } from '@phosphor-icons/react';
import type { Page } from '@regia/shared';
import { Link } from 'react-router';
import { PLATFORM_LABELS, PlatformIcon } from '../../lib/platforms';
import { Badge, cn, ListGroup, ListRow, Section, Skeleton, Stat } from '../../ui';
import { useSummary } from './api';

const STATUS_LABEL = {
  da_collegare: 'Da collegare',
  collegato: 'Collegato',
  errore: 'Errore',
} as const;
const STATUS_TONE = { da_collegare: 'neutral', collegato: 'info', errore: 'alert' } as const;

function SetupChecklist({ page }: { page: Page }) {
  const steps = [
    {
      done: page.channels.length > 0,
      label: 'Aggiungi i canali: Facebook, Instagram, scheda Google, sito',
      to: 'info',
    },
    { done: Boolean(page.logoUrl), label: 'Carica il logo', to: 'info' },
    {
      done: Boolean(page.tone && page.audience),
      label: 'Scrivi tono di voce e pubblico: servono all’AI per scrivere i post',
      to: 'info',
    },
    {
      done: page.channels.some((c) => c.postsPerWeek),
      label: 'Imposta quanti post a settimana: Regia ti ricorderà quando postare',
      to: 'info',
    },
  ];
  if (steps.every((s) => s.done)) return null;
  return (
    <Section title="Per iniziare">
      <ListGroup>
        {steps.map((s) => (
          <ListRow
            key={s.label}
            to={s.done ? undefined : s.to}
            chevron={!s.done}
            wrap
            leading={
              s.done ? (
                <CheckCircleIcon size={24} weight="fill" className="text-ok" aria-label="Fatto" />
              ) : (
                <CircleIcon size={24} className="text-ink-3" aria-label="Da fare" />
              )
            }
            title={
              <span className={cn(s.done && 'text-ink-3 line-through decoration-1')}>
                {s.label}
              </span>
            }
          />
        ))}
      </ListGroup>
    </Section>
  );
}

export function SchedaOverview({ page }: { page: Page }) {
  const summary = useSummary(page.id);
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-x-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
      <div>
        {summary.isLoading ? (
          <Skeleton className="mb-8 h-28 w-full rounded-[var(--radius-sheet)]" />
        ) : summary.data && summary.data.length > 0 ? (
          <Section title="In breve">
            <div className="grid grid-cols-2 gap-x-6 gap-y-5 rounded-[var(--radius-sheet)] bg-sheet p-5 sm:grid-cols-3">
              {summary.data.map((item) =>
                item.href ? (
                  <Link key={item.id} to={item.href} className="rounded-lg hover:opacity-80">
                    <Stat label={item.label} value={item.value} delta={item.delta} />
                  </Link>
                ) : (
                  <Stat key={item.id} label={item.label} value={item.value} delta={item.delta} />
                ),
              )}
            </div>
          </Section>
        ) : null}
        <SetupChecklist page={page} />
      </div>
      <div>
        <Section title="Canali">
          {page.channels.length === 0 ? (
            <p className="px-1 text-[0.9375rem] text-ink-2">
              Nessun canale.{' '}
              <Link to="info" className="font-semibold text-action hover:underline">
                Aggiungine uno
              </Link>
            </p>
          ) : (
            <ListGroup>
              {page.channels.map((c) => (
                <ListRow
                  key={c.id}
                  to="info"
                  leading={<PlatformIcon platform={c.platform} size={22} className="text-ink" />}
                  title={c.label || c.handle || PLATFORM_LABELS[c.platform]}
                  detail={
                    c.postsPerWeek
                      ? `${c.postsPerWeek} post a settimana`
                      : PLATFORM_LABELS[c.platform]
                  }
                  trailing={<Badge tone={STATUS_TONE[c.status]}>{STATUS_LABEL[c.status]}</Badge>}
                />
              ))}
            </ListGroup>
          )}
        </Section>
      </div>
    </div>
  );
}
